import React, { useState, useRef, useEffect } from "react";
import { Bot, Send, X, Sparkles, User, AlertCircle } from "lucide-react";
import api, { fmtErr } from "../api/axios";

/**
 * AskJarvisModal — internal Sales/CRM AI assistant.
 *
 * Open from any role's sidebar. Powered by Claude Sonnet 4.5 via the
 * Emergent LLM key on the backend. The system prompt is sales-focused and
 * grounded in LIVE CRM data: programs, payment plans, offers, lead status.
 *
 * Does NOT handle student support, tickets, or LMS — those live in the
 * Support App. This is for the SALES TEAM only.
 *
 * Mention any 10-digit phone in your message → backend auto-fetches that
 * lead's full snapshot (status, payments, EMIs, deadlines) and grounds
 * Claude's answer in real data.
 */
const SUGGESTIONS = [
  "What payment plans do we offer?",
  "Show me the 100-day EMI structure for HCP",
  "What's the lead status for 9876543210?",
  "Compare HCP and Snehworld Premium pricing",
];

export default function AskJarvisModal({ onClose, user }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const endRef = useRef(null);
  const inputRef = useRef(null);
  const sessionId = useRef(`crm-${user?.id || "anon"}-${Date.now()}`);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  useEffect(() => {
    setTimeout(() => inputRef.current?.focus(), 100);
  }, []);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    setError("");
    const next = [...messages, { role: "user", content: msg }];
    setMessages(next);
    setInput("");
    setLoading(true);
    try {
      const res = await api.post("/support/internal-jarvis/chat", {
        message: msg,
        session_id: sessionId.current,
        history: messages, // send PRIOR history (not including this new msg)
      });
      setMessages([...next, {
        role: "assistant",
        content: res.data.reply,
        matched_phone: res.data.matched_phone || "",
      }]);
    } catch (e) {
      setError(fmtErr(e.response?.data?.detail) || "Jarvis is unavailable right now");
    } finally {
      setLoading(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <div className="jarvis-chat-overlay" data-testid="ask-jarvis-modal">
      <div className="jarvis-chat-box" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="jarvis-chat-head">
          <div className="jarvis-chat-icon">
            <Bot size={18} />
          </div>
          <div style={{ flex: 1 }}>
            <div className="jarvis-chat-title">Ask Jarvis</div>
            <div className="jarvis-chat-sub">
              <Sparkles size={11} /> Internal sales assistant • Live CRM data
            </div>
          </div>
          <button
            className="icon-btn"
            onClick={onClose}
            data-testid="ask-jarvis-close"
            style={{ color: "#fff" }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Conversation */}
        <div className="jarvis-chat-body" data-testid="ask-jarvis-body">
          {messages.length === 0 && !loading && (
            <div className="jarvis-welcome">
              <Bot size={36} style={{ color: "#a5b4fc" }} />
              <div className="jarvis-welcome-title">
                Hi {user?.name?.split(" ")[0] || "there"}! I can help with:
              </div>
              <ul className="jarvis-welcome-list">
                <li>Program pricing, GST, total payable</li>
                <li>Payment plan structure (DP + EMIs)</li>
                <li>Discounts and offers per program</li>
                <li>Customer payment status (give me a phone number)</li>
                <li>Lead funnel position (stage, status, last activity)</li>
              </ul>
              <div className="jarvis-suggestions">
                {SUGGESTIONS.map((s, i) => (
                  <button
                    key={i}
                    className="jarvis-suggestion-chip"
                    onClick={() => send(s)}
                    data-testid={`jarvis-suggestion-${i}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
              <div className="jarvis-disclaimer">
                <AlertCircle size={11} />
                I don't handle student support, session links, or LMS — that's the Support App's job.
              </div>
            </div>
          )}

          {messages.map((m, i) => (
            <div
              key={i}
              className={`jarvis-msg jarvis-msg-${m.role}`}
              data-testid={`jarvis-msg-${i}`}
            >
              <div className="jarvis-msg-avatar">
                {m.role === "user" ? <User size={14} /> : <Bot size={14} />}
              </div>
              <div className="jarvis-msg-content">
                {m.role === "assistant" && m.matched_phone && (
                  <div className="jarvis-match-pill">
                    🔍 Auto-fetched lead for {m.matched_phone}
                  </div>
                )}
                <div className="jarvis-msg-text">{m.content}</div>
              </div>
            </div>
          ))}

          {loading && (
            <div className="jarvis-msg jarvis-msg-assistant">
              <div className="jarvis-msg-avatar"><Bot size={14} /></div>
              <div className="jarvis-msg-content">
                <div className="jarvis-typing">
                  <span></span><span></span><span></span>
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="error-box" data-testid="ask-jarvis-error">{error}</div>
          )}
          <div ref={endRef} />
        </div>

        {/* Input */}
        <div className="jarvis-chat-input-row">
          <textarea
            ref={inputRef}
            className="jarvis-chat-input"
            placeholder="Ask anything about pricing, plans, leads, payments…"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKey}
            rows={1}
            disabled={loading}
            data-testid="ask-jarvis-input"
          />
          <button
            className="jarvis-chat-send"
            onClick={() => send()}
            disabled={loading || !input.trim()}
            data-testid="ask-jarvis-send"
            title="Send"
          >
            <Send size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
